<template>
  <div class="preika-insight-page">
    <section class="preika-page-hero preika-page-hero--profit">
      <div>
        <p class="preika-page-hero__eyebrow">PROJECT PROFIT FORECAST</p>
        <h1>プロジェクト収益予測</h1>
        <p>売上管理の実績と月次単価をつなぎ、プロジェクト別の売上・原価・粗利の着地を確認します。</p>
      </div>
      <div class="preika-page-controls">
        <label><span>開始月</span><input v-model="fromMonth" type="month" @change="fetchData"></label>
        <label><span>表示期間</span>
          <select v-model.number="rangeMonths" @change="fetchData">
            <option :value="6">6か月</option>
            <option :value="12">12か月</option>
            <option :value="18">18か月</option>
          </select>
        </label>
        <button type="button" class="preika-btn preika-btn--primary" :disabled="loading" @click="fetchData"><CIcon name="cil-loop"/>再予測</button>
      </div>
    </section>

    <div v-if="loading" class="preika-analytics-loading"><span class="preika-analytics-spinner"></span>収益予測を計算しています</div>
    <div v-else-if="error" class="preika-analytics-error" role="alert">
      <div><strong>プロジェクト収益予測を取得できませんでした</strong><small>{{ error }}</small></div>
      <button type="button" @click="fetchData">再取得</button>
    </div>

    <template v-else>
      <section class="preika-analytics-kpis preika-analytics-kpis--four">
        <article class="preika-analytics-kpi">
          <span class="preika-analytics-kpi__icon"><CIcon name="cil-yen"/></span>
          <small>期間売上</small><strong>{{ money(data.totalRevenue) }}</strong><p>実績と予測の合計</p>
        </article>
        <article class="preika-analytics-kpi">
          <span class="preika-analytics-kpi__icon"><CIcon name="cil-wallet"/></span>
          <small>期間原価</small><strong>{{ money(data.totalCost) }}</strong><p>メンバー原価の合計</p>
        </article>
        <article class="preika-analytics-kpi" :class="{ 'preika-analytics-kpi--danger': Number(data.totalGrossProfit) < 0 }">
          <span class="preika-analytics-kpi__icon"><CIcon name="cil-chart-pie"/></span>
          <small>期間粗利</small><strong>{{ money(data.totalGrossProfit) }}</strong><p>売上 − 原価</p>
        </article>
        <article class="preika-analytics-kpi">
          <span class="preika-analytics-kpi__icon"><CIcon name="cil-speedometer"/></span>
          <small>粗利率</small><strong>{{ percent(data.grossMargin) }}</strong><p>{{ data.projects.length }}プロジェクト</p>
        </article>
      </section>

      <section class="preika-analytics-card">
        <header class="preika-analytics-card__header">
          <div><p>MONTHLY OUTLOOK</p><h2>月別の売上・原価・粗利</h2></div>
          <div class="preika-finance-legend"><span><i class="revenue"></i>売上</span><span><i class="cost"></i>原価</span><span><i class="profit"></i>粗利</span></div>
        </header>

        <div v-if="months.length" class="preika-finance-chart" :style="{ '--preika-chart-columns': months.length }">
          <article v-for="month in months" :key="month.yearMonth" class="preika-finance-chart__column">
            <div class="preika-finance-chart__plot">
              <span class="preika-finance-bar preika-finance-bar--revenue" :style="{ height: chartHeight(month.revenue) }" :title="'売上 ' + money(month.revenue)"></span>
              <span class="preika-finance-bar preika-finance-bar--cost" :style="{ height: chartHeight(month.cost) }" :title="'原価 ' + money(month.cost)"></span>
              <span class="preika-finance-profit" :class="{ negative: Number(month.grossProfit) < 0 }">{{ compactMoney(month.grossProfit) }}</span>
            </div>
            <strong>{{ compactMonth(month.yearMonth) }}</strong>
            <span class="preika-source-badge" :class="'preika-source-badge--' + sourceTone(month.source)">{{ sourceLabel(month.source) }}</span>
          </article>
        </div>
        <div v-else class="preika-analytics-empty"><CIcon name="cil-chart-pie"/><strong>表示期間の収益データがありません</strong><p>売上登録またはプロジェクト単価設定を確認してください。</p></div>
      </section>

      <section class="preika-analytics-card">
        <header class="preika-analytics-card__header">
          <div><p>PROJECT OUTLOOK</p><h2>プロジェクト別の収益予測</h2></div>
          <div class="preika-analytics-actions">
            <router-link to="/admin/project-rate" class="preika-text-link">単価設定<CIcon name="cil-arrow-right"/></router-link>
            <router-link to="/admin/total/list_project" class="preika-text-link">プロジェクト集計<CIcon name="cil-arrow-right"/></router-link>
          </div>
        </header>

        <div v-if="projects.length" class="preika-table-scroll">
          <table class="preika-data-table preika-profit-table">
            <thead>
              <tr>
                <th>プロジェクト</th>
                <th>期間売上</th>
                <th>期間原価</th>
                <th>期間粗利</th>
                <th>粗利率</th>
                <th v-for="month in months" :key="month.yearMonth">{{ compactMonth(month.yearMonth) }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="project in projects" :key="project.projectId">
                <th scope="row"><strong>{{ project.projectName }}</strong><small>{{ project.companyName }}</small></th>
                <td>{{ money(project.totalRevenue) }}</td>
                <td>{{ money(project.totalCost) }}</td>
                <td :class="{ 'preika-negative-value': Number(project.totalGrossProfit) < 0 }"><strong>{{ money(project.totalGrossProfit) }}</strong></td>
                <td><span class="preika-margin-badge" :class="marginTone(project.grossMargin)">{{ percent(project.grossMargin) }}</span></td>
                <td v-for="value in project.months" :key="value.yearMonth" class="preika-profit-table__month">
                  <strong :class="{ 'preika-negative-value': Number(value.grossProfit) < 0 }">{{ compactMoney(value.grossProfit) }}</strong>
                  <span class="preika-source-dot" :class="'preika-source-dot--' + sourceTone(value.source)" :title="sourceLabel(value.source)"></span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="preika-analytics-empty preika-analytics-empty--compact">表示対象のプロジェクトはありません。</div>
      </section>

      <p class="preika-analytics-note"><CIcon name="cil-lightbulb"/>{{ data.note }}</p>
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import {
  compactManagementYearMonth,
  financialSourceLabel,
  financialSourceTone,
  formatManagementMoney,
  formatManagementPercent
} from '../../utils/managementInsights'

export default {
  name: 'ProjectProfitForecast',

  data () {
    return {
      fromMonth: moment().format('YYYY-MM'),
      rangeMonths: 12,
      loading: false,
      error: '',
      data: { totalRevenue: 0, totalCost: 0, totalGrossProfit: 0, grossMargin: 0, note: '', months: [], projects: [] }
    }
  },

  computed: {
    months () { return Array.isArray(this.data.months) ? this.data.months : [] },
    projects () { return Array.isArray(this.data.projects) ? this.data.projects : [] },
    chartMaximum () {
      return Math.max(1, ...this.months.map(item => Math.max(Number(item.revenue || 0), Number(item.cost || 0))))
    }
  },

  mounted () { this.fetchData() },

  methods: {
    money: formatManagementMoney,
    percent: formatManagementPercent,
    compactMonth: compactManagementYearMonth,
    sourceLabel: financialSourceLabel,
    sourceTone: financialSourceTone,

    fetchData () {
      const target = moment(this.fromMonth, 'YYYY-MM', true)
      if (!target.isValid()) return
      this.loading = true
      this.error = ''
      return axios
        .get(process.env.VUE_APP_API_ORIGIN + '/management/project-profit-forecast', {
          params: { from: target.format('YYYYMM'), months: this.rangeMonths }
        })
        .then(response => {
          const value = response.data || {}
          this.data = {
            ...value,
            months: Array.isArray(value.months) ? value.months : [],
            projects: Array.isArray(value.projects) ? value.projects : []
          }
        })
        .catch(error => {
          this.error = error.response && error.response.status === 403
            ? '管理者権限が必要です。'
            : '通信または収益予測処理でエラーが発生しました。'
          console.error('プロジェクト収益予測取得失敗:', error)
        })
        .finally(() => { this.loading = false })
    },

    chartHeight (value) {
      return `${Math.max(2, Number(value || 0) / this.chartMaximum * 100)}%`
    },

    compactMoney (value) {
      const numeric = Number(value || 0)
      const sign = numeric < 0 ? '-' : ''
      const absolute = Math.abs(numeric)
      if (absolute >= 100000000) return `${sign}${(absolute / 100000000).toFixed(1)}億`
      if (absolute >= 10000) return `${sign}${Math.round(absolute / 10000).toLocaleString('ja-JP')}万`
      return `${sign}${Math.round(absolute).toLocaleString('ja-JP')}`
    },

    marginTone (value) {
      const numeric = Number(value || 0)
      if (numeric < 0) return 'danger'
      if (numeric < 20) return 'warning'
      if (numeric >= 35) return 'good'
      return 'neutral'
    }
  }
}
</script>
